import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { TableMenu } from "components";
import { IOption } from "components/TableMenu/TableMenuInterfaces";
import { IApiAccessCorporation } from "../ApiAccessOversightInterfaces";
import { useStyles } from "../ApiAccessOversight.styles";

interface IApiAccessColumns extends Omit<IMainTableColumn, "render"> {
    render: (row: IApiAccessCorporation) => string | JSX.Element | number;
}

/**
 * List columns mirroring useCorporationsColumns (status badge + TableMenu options).
 * The enable/disable option fires the toggle DIRECTLY — there is NO confirm modal on
 * toggle-off (contract §10.3 / UX Flow H); the page helper text carries the warning.
 */
const useApiAccessColumns = (
    getApiAccessStatus: (row: IApiAccessCorporation) => boolean,
    onToggleClick: (row: IApiAccessCorporation, enable: boolean) => void,
    onViewKeysClick: (row: IApiAccessCorporation) => void,
    toggling: boolean,
): IApiAccessColumns[] => {
    const classes = useStyles();
    const { t } = useTranslation();

    const renderToggleOption = useCallback(
        (row: IApiAccessCorporation): IOption => {
            const enabled = getApiAccessStatus(row);
            return {
                name: enabled ? t("api_access.disable") : t("api_access.enable"),
                callback: () => onToggleClick(row, !enabled),
                customStyles: { fontWeight: 600 },
                // Disable while a toggle mutation is in flight to prevent a double-fire /
                // enable<->disable race that could leave the gate ON with all keys revoked.
                disabled: toggling,
            };
        },
        [getApiAccessStatus, onToggleClick, t, toggling],
    );

    return useMemo(
        (): IApiAccessColumns[] => [
            {
                id: "name",
                label: t("api_access.table_headers.name"),
                hideSortIcon: false,
                render: (row) => row.name,
            },
            {
                id: "status",
                label: t("api_access.table_headers.status"),
                hideSortIcon: true,
                render: (row) => (
                    <span className={classes.statusButton}>
                        {getApiAccessStatus(row) ? t("api_access.enabled") : t("api_access.disabled")}
                    </span>
                ),
            },
            {
                id: "active_keys",
                label: t("api_access.table_headers.active_keys"),
                hideSortIcon: true,
                render: (row) => row.third_party_api_keys_aggregate?.aggregate?.count ?? 0,
            },
            {
                id: "last_used",
                label: t("api_access.table_headers.last_used"),
                hideSortIcon: true,
                render: (row) => {
                    const lastUsed = row.third_party_api_keys_aggregate?.aggregate?.max?.last_used_at;
                    return lastUsed ? moment(lastUsed).format("YYYY/MM/DD HH:mm") : t("api_access.never");
                },
            },
            {
                id: "options",
                label: "",
                hideSortIcon: true,
                render: (row) => {
                    const options: IOption[] = [
                        renderToggleOption(row),
                        {
                            name: t("api_access.view_keys"),
                            callback: () => onViewKeysClick(row),
                        },
                    ];
                    return <TableMenu options={options} />;
                },
            },
        ],
        [t, classes.statusButton, getApiAccessStatus, renderToggleOption, onViewKeysClick],
    );
};

export default useApiAccessColumns;
